import { useMutation } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import { AnalyticsEvent, Tracker } from "../analytics";
import { useAuthContext } from "../contexts/AuthContext";
import { requestCompleteSignUp } from "../services/completeSignUp";
import Checkbox from "./Checkbox";
import Container from "./Container";
import Text from "./Text";

export default function CompleteSignUpForm() {
    const auth = useAuthContext();
    const toast = useRef<Toast | null>(null);
    const [phone, setPhone] = useState("");
    const [skipPhone, setSkipPhone] = useState(false);

    const canSubmit = skipPhone || phone.replace(/\D/g, "").length >= 10;

    const mutation = useMutation({
        mutationFn: async () => {
            Tracker.track(
                AnalyticsEvent.AuthSignUpPhoneSubmitted,
                skipPhone
                    ? { phone_skipped: true }
                    : { phone_skipped: false, country_ddi: 55 },
            );

            return requestCompleteSignUp(
                skipPhone
                    ? { skipPhone: true }
                    : { skipPhone: false, ddi: 55, phone: phone.replace(/\D/g, "") },
            );
        },
        onSuccess: ({ idToken, refreshToken }) => {
            Tracker.trackSignUp("completed");
            auth.finishRegistration(idToken, refreshToken);
            toast.current?.show({
                severity: "success",
                summary: "Cadastro concluído",
            });
        },
        onError: (error: Error) => {
            toast.current?.show({
                severity: "error",
                summary: error.message || "Não foi possível concluir o cadastro",
            });
        },
    });

    return (
        <>
            <Toast ref={toast} />

            <Container flexColumn fullWidth maxWidth={500} paddingHorizontal="6" paddingVertical="3" gap={3}>
                <Text size="xl" fontWeight="bold">
                    Complete seu cadastro
                </Text>
                <Text size="sm" color="400">
                    Para continuar no portal, informe um telefone ou pule esta etapa.
                </Text>

                <div className="w-full">
                    <label htmlFor="complete-signup-phone" className="block text-sm mb-2">
                        Telefone (Brasil +55)
                    </label>
                    <InputText
                        id="complete-signup-phone"
                        className="w-full"
                        inputMode="tel"
                        value={phone}
                        disabled={skipPhone || mutation.isPending}
                        placeholder="11999999999"
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </div>

                <Checkbox
                    id="skip-phone"
                    name="skipPhone"
                    label="Prefiro informar o telefone depois"
                    checked={skipPhone}
                    disabled={mutation.isPending}
                    onChange={(checked) => {
                        setSkipPhone(checked);
                        if (checked) {
                            setPhone("");
                        }
                    }}
                />

                <Button
                    label={mutation.isPending ? "Concluindo..." : "Continuar"}
                    className="bg-primary w-full"
                    loading={mutation.isPending}
                    disabled={!canSubmit || mutation.isPending}
                    onClick={() => mutation.mutate()}
                />

                {mutation.error && (
                    <Message className="w-full" severity="error" text={mutation.error.message} />
                )}

                <Button
                    label="Sair"
                    type="button"
                    text
                    className="w-full"
                    disabled={mutation.isPending}
                    onClick={() => void auth.logout()}
                />
            </Container>
        </>
    );
}
